import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import InitialsAvatar from "@/components/base/InitialsAvatar";
import WorkCard from "@/components/base/WorkCard";
import { getAllTopics, getReviewsByTopic, getTopicById, getWorkById, getWorksByIds } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function ReviewTopic() {
  const { topicId } = useParams<{ topicId: string }>();
  const topic = topicId ? getTopicById(topicId) : undefined;
  const [openId, setOpenId] = useState<string | null>(null);

  usePageMeta(
    topic ? `${topic.title} - 疯狂的欲望影评专题` : "影评专题 - 疯狂的欲望",
    topic ? topic.intro.slice(0, 100) : undefined,
  );

  if (!topic) {
    return (
      <div className="mx-auto flex min-h-[60vh] w-full max-w-[1400px] flex-col items-center justify-center px-4 text-center">
        <i className="ri-book-2-line text-5xl text-foreground-400" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">这个影评专题暂时不存在</h1>
        <p className="mt-2 text-sm text-foreground-600">换个专题继续阅读，或者回到影评首页。</p>
        <Link to="/review/topic/t1" className="mt-6 cursor-pointer rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700">
          查看推荐专题
        </Link>
      </div>
    );
  }

  const reviews = getReviewsByTopic(topic.id);
  const works = getWorksByIds(topic.workIds);
  const otherTopics = getAllTopics().filter((t) => t.id !== topic.id);

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img src={topic.cover} alt={topic.title} title={topic.title} className="absolute inset-0 h-full w-full object-cover object-top opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/60" />
        <div className="relative mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-20">
          <nav className="mb-5 flex flex-wrap items-center gap-2 text-xs text-background-300">
            <Link to="/" className="cursor-pointer hover:text-background-50">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-100">影评专题</span>
          </nav>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-950">
            <i className="ri-book-open-line" />
            影评专题
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-2xl font-black leading-snug text-background-50 md:text-4xl">
            {topic.title}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">{topic.intro}</p>
          <p className="mt-5 text-sm text-background-300">共收录 {reviews.length} 篇影评 · {works.length} 部相关作品</p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-16">
        <div className="flex flex-col gap-10 lg:flex-row">
          <div className="min-w-0 flex-1">
            <h2 className="mb-6 flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                <i className="ri-article-line" />
              </span>
              影评合集
            </h2>

            <div className="space-y-5">
              {reviews.map((review) => {
                const work = getWorkById(review.workId);
                const isOpen = openId === review.id;
                return (
                  <article key={review.id} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                    <div className="p-5 md:p-6">
                      <div className="flex items-start gap-4">
                        <InitialsAvatar name={review.author} size={48} className="bg-accent-100 text-accent-700" />
                        <div className="min-w-0 flex-1">
                          <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950 md:text-xl">
                            {review.title}
                          </h3>
                          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                            <span className="font-medium text-accent-700">{review.author}</span>
                            {work && (
                              <Link to={`/detail/${work.id}`} className="cursor-pointer text-primary-700 hover:underline">
                                《{work.title}》
                              </Link>
                            )}
                            <span>· {review.date}</span>
                            <span>· 阅读约 {review.readTime} 分钟</span>
                          </div>
                        </div>
                      </div>

                      <p className="mt-4 text-sm leading-relaxed text-foreground-700">{review.excerpt}</p>

                      {isOpen && (
                        <div className="mt-4 space-y-3 border-t border-background-200 pt-4">
                          {review.content.map((para, i) => (
                            <p key={i} className="text-sm leading-loose text-foreground-700 md:text-base">
                              {para}
                            </p>
                          ))}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => setOpenId(isOpen ? null : review.id)}
                        className="mt-4 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                      >
                        {isOpen ? "收起全文" : "阅读全文"}
                        <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <aside className="w-full shrink-0 lg:w-80">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-base font-bold text-foreground-950">相关影视作品</h3>
                <ul className="mt-4 space-y-3">
                  {works.map((w) => (
                    <li key={w.id}>
                      <Link to={`/detail/${w.id}`} className="group flex cursor-pointer items-center gap-3">
                        <div className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
                          <img src={w.cover} alt={w.title} title={w.title} className="h-full w-full object-cover object-top" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground-950 group-hover:text-primary-700">{w.title}</p>
                          <p className="mt-0.5 text-xs text-foreground-500">{w.year} · {w.genres.join(" / ")}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg bg-secondary-100 p-5">
                <h3 className="font-heading text-base font-bold text-secondary-900">其他影评专题</h3>
                <ul className="mt-3 space-y-2.5">
                  {otherTopics.map((t) => (
                    <li key={t.id}>
                      <Link to={`/review/topic/${t.id}`} className="flex cursor-pointer items-start gap-2 text-sm text-secondary-900 transition-colors hover:text-primary-700">
                        <i className="ri-arrow-right-s-line mt-0.5 shrink-0" />
                        <span>{t.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-16">
          <h2 className="mb-6 flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-700">
              <i className="ri-movie-2-line" />
            </span>
            专题关联作品
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6">
            {works.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}